import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import SafeIcon from '../../common/SafeIcon';
import LocalizedLink from '../common/LocalizedLink';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';

const { FiHeart, FiInstagram, FiFacebook, FiGlobe } = FiIcons;
const { BsThreads } = BsIcons;

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    {
      name: 'Threads',
      url: 'https://www.threads.com/@homy.hk',
      icon: BsThreads,
      label: 'Threads'
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/homy.hk',
      icon: FiInstagram,
      label: 'Instagram'
    },
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61578745554661',
      icon: FiFacebook,
      label: 'Facebook'
    }
  ];

  // 🔧 FIX: 添加 Blog 相關鏈接，確保所有重要頁面都能被爬蟲發現
  const blogCategories = [
    { key: 'health', label: t('blog.categories.health') || 'Health' },
    { key: 'nutrition', label: t('blog.categories.nutrition') || 'Nutrition' },
    { key: 'training', label: t('blog.categories.training') || 'Training' },
    { key: 'grooming', label: t('blog.categories.grooming') || 'Grooming' }
  ];

  // 語言切換鏈接
  const languageLinks = [
    { code: 'en', name: 'English' },
    { code: 'zh', name: '繁體中文' },
    { code: 'ja', name: '日本語' }
  ];

  return (
    <footer className="bg-gradient-to-br from-neutral-900 to-neutral-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center mr-3">
                <SafeIcon icon={FiHeart} className="text-xl text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Pet Care Note</span>
            </div>
            <p className="text-neutral-300 mb-6 max-w-md leading-relaxed">
              {t('footer.footerDescription')}
            </p>
            
            {/* 🔧 FIX: 添加語言支持提示 */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-neutral-400 text-sm mb-2">
                <SafeIcon icon={FiGlobe} className="text-primary-400" />
                <span>{t('footer.availableLanguages') || 'Available in'}:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {languageLinks.map((lang) => (
                  <a
                    key={lang.code}
                    href={`/#/${lang.code}`}
                    className={`px-3 py-1 rounded-md text-sm transition-colors duration-200 ${
                      language === lang.code
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-700 text-neutral-300 hover:bg-neutral-600'
                    }`}
                    hrefLang={lang.code}
                  >
                    {lang.name}
                  </a>
                ))}
              </div>
            </div>

            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a 
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-neutral-700 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-colors duration-200"
                  aria-label={social.label}
                >
                  <SafeIcon icon={social.icon} className="text-white" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">{t('footer.quickLinks')}</h3>
            <ul className="space-y-3">
              <li>
                <LocalizedLink 
                  to="/" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('navbar.home')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink 
                  to="/features" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('navbar.features')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink 
                  to="/about" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('navbar.about')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink 
                  to="/contact" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('navbar.contact')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink 
                  to="/dashboard" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('navbar.dashboard')}
                </LocalizedLink>
              </li>
            </ul>
          </div>

          {/* 🔧 FIX: 添加 Blog 區塊 */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">{t('navbar.blog') || 'Blog'}</h3>
            <ul className="space-y-3">
              <li>
                <LocalizedLink 
                  to="/blog" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('blog.allArticles') || 'All Articles'}
                </LocalizedLink>
              </li>
              {blogCategories.map((category) => (
                <li key={category.key}>
                  <LocalizedLink 
                    to={`/blog?category=${category.key}`}
                    className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                  >
                    {category.label}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Support */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">{t('footer.support')}</h3>
            <ul className="space-y-3">
              <li>
                <LocalizedLink 
                  to="/privacy" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('footer.privacyPolicy')}
                </LocalizedLink>
              </li>
              <li>
                <LocalizedLink 
                  to="/terms" 
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  {t('footer.termsOfService')}
                </LocalizedLink>
              </li>
              <li>
                <a 
                  href="https://homy.hk" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-primary-400 transition-colors duration-200"
                >
                  Homy Tech Limited
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-neutral-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-neutral-400 text-sm flex items-center gap-2 flex-wrap justify-center md:justify-start">
              © {currentYear} Pet Care Note
              <SafeIcon icon={FiHeart} className="text-primary-500 animate-pulse" />
              <a 
                href="https://homy.hk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 transition-colors"
              >
                Homy Tech Limited
              </a>
            </p>
            <p className="text-neutral-400 text-sm flex items-center gap-1">
              {t('footer.craftedWith')}
              <SafeIcon icon={FiHeart} className="text-primary-500 animate-pulse" />
            </p>
          </div>
          
          {/* 🔧 FIX: 添加 SEO 友好的語言切換鏈接 */}
          <div className="mt-4 text-center">
            <p className="text-neutral-500 text-xs">
              {t('footer.multiLanguageSupport') || 'Multi-language support'}: 
              <a href="/#/en" className="text-neutral-400 hover:text-primary-400 ml-2" hrefLang="en">English</a>
              {' | '}
              <a href="/#/zh" className="text-neutral-400 hover:text-primary-400" hrefLang="zh">繁體中文</a>
              {' | '}
              <a href="/#/ja" className="text-neutral-400 hover:text-primary-400" hrefLang="ja">日本語</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;